import React, { useState} from 'react';
import {
  Box,
  Button,
  Grid,
  Typography,
  Stack,
  Paper,
  FormControl,
  FormHelperText,
  TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import '../../Styles/CategoryFormStyles'
import * as Yup from 'yup';
import { categoryStyles } from '../../Styles/CategoryFormStyles';
import { useAtom } from 'jotai';
import { itemSubCategoryAtom } from '../ManageItemCategory/ItemCategoryList';
import {
  CreateItemSubCategoryRequest,
  CreateItemSubCategoryRequestStoreTypeEnum,
  CreateItemSubCategoryRequestAddedByEnum,
} from '../../../client-sdk';
import {
  useItemSubCategoryAdd,
  useEditItemSubCategory,
} from '../../Query/ItemSubCategoryQuery';
import { primary } from '../../../theme';
import { useTranslation } from 'react-i18next';

const ItemSubCategoryForm = ({
  mode,
  onCancel,
  initialData,
}: {
  mode: 'add' | 'edit';
  onCancel: () => void;
  initialData?: {
    id: string;
    englishName: string;
    arabicName: string;
    addedBy: string;
    storeId?: string;
    storeType?: string;
  };
}) => {
  const { t } = useTranslation();
  const { mutate } = useItemSubCategoryAdd();
  const { mutate: editItemSubCategory } = useEditItemSubCategory();
  const [loading, setLoading] = useState(false);
  const [categoryId] = useAtom(itemSubCategoryAtom);
  // const [setStoreOptions] = useState<{ label: string; value: string; storeType: string }[]>([]);
  // const { params } = useManageCmsParams();
  // const isReady = Boolean(params && Object.keys(params).length > 0);
  // const memoizedParams = useMemo(() => {
  //   return isReady ? params : {};
  // }, [isReady, params]);
  // Fetch stores data
  // const { data: storesData } = useAllStores(memoizedParams);

  // useEffect(() => {
  //   if (storesData && Array.isArray(storesData.data)) {
  //     const options = storesData.data.map((store: any) => ({
  //       value: store.id,
  //       label: store.nameEnglish,
  //       storeType: store.restaurantDetails?.storeType || '',
  //     }));
  //     setStoreOptions(options);
  //   } else {
  //     setStoreOptions([]);
  //   }
  // }, [storesData]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      englishName: initialData?.englishName || '',
      arabicName: initialData?.arabicName || '',
    },
    validationSchema: Yup.object().shape({
      englishName: Yup.string()
        .required(t('itemSubCategory.form.validation.requiredCategoryNameEnglish'))
        .max(100, t('itemSubCategory.form.validation.categoryNameEnglishMaxLength'))
        .matches(
          /^[A-Za-z][A-Za-z0-9\s!@#$%^&*(),.?":{}|<>_-]*$/,
          t('itemSubCategory.form.validation.categoryNameEnglishFormat')
        )
        .test(
          'not-only-numbers',
          t('itemSubCategory.form.validation.categoryNameEnglishNotOnlyNumbers'),
          (value) => {
            if (!value) return false;
            return !/^\d+$/.test(value.trim());
          }
        )
        .test(
          'not-only-spaces',
          t('itemSubCategory.form.validation.categoryNameEnglishNotOnlySpaces'),
          (value) => {
            if (!value) return false;
            return value.trim().length > 0;
          }
        ),

      arabicName: Yup.string()
        .required(t('itemSubCategory.form.validation.requiredCategoryNameArabic'))
        .max(50, t('itemSubCategory.form.validation.categoryNameArabicMaxLength'))
        .matches(
          /^[\u0600-\u06FF][\u0600-\u06FF\s]*$/,
          t('itemSubCategory.form.validation.categoryNameArabicFormat')
        )
        .test(
          'not-only-spaces',
          t('itemSubCategory.form.validation.categoryNameArabicNotOnlySpaces'),
          (value) => {
            if (typeof value !== 'string') return false;
            return value.trim().length > 0;
          }
        ),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);

        if (!categoryId) {
          console.error('categoryId is missing:', categoryId);
          setLoading(false);
          return;
        }

        console.log('Form values:', values);
        console.log('categoryId:', categoryId);

        const getStoreType = (storeType?: string): CreateItemSubCategoryRequestStoreTypeEnum => {
          if (!storeType) return CreateItemSubCategoryRequestStoreTypeEnum.Restaurant;

          switch (storeType.toLowerCase()) {
            case 'restaurant':
              return CreateItemSubCategoryRequestStoreTypeEnum.Restaurant;
            case 'grocery':
            case 'grocerystore':
              return CreateItemSubCategoryRequestStoreTypeEnum.GroceryStore;
            case 'pharmacy':
            case 'pharmacystore':
              return CreateItemSubCategoryRequestStoreTypeEnum.PharmacyStore;
            default:
              return CreateItemSubCategoryRequestStoreTypeEnum.Restaurant;
          }
        };

        const itemSubCategory: CreateItemSubCategoryRequest = {
          addedBy: CreateItemSubCategoryRequestAddedByEnum.Merchant,
          nameEnglish: values.englishName,
          nameArabic: values.arabicName,
          parentCategoryId: categoryId.id,
          storeType: getStoreType(categoryId.storeType),
          storeId: categoryId.storeId || '',
        };

        const itemEditSubCategory: CreateItemSubCategoryRequest = {
          addedBy: CreateItemSubCategoryRequestAddedByEnum.Merchant,
          nameEnglish: values.englishName,
          nameArabic: values.arabicName,
          parentCategoryId: categoryId.id,
          storeType: getStoreType(categoryId.storeType),
          storeId: categoryId.storeId || '',
        };
        if (mode === 'edit' && initialData?.id) {
          editItemSubCategory(
            { id: initialData?.id, data: itemEditSubCategory },
            {
              onSuccess: () => {
                //   showMessage('Item Category Updated Successfully', 'success');
                formik.resetForm();
                setLoading(false);
                onCancel();
              },
              onError: (error) => {
                console.error('Edit Error:', error);
                setLoading(false);
              },
            }
          );
        } else {
          // This is for "add" mode
          mutate(itemSubCategory, {
            onSuccess: () => {
              formik.resetForm();
              setLoading(false);
              onCancel();
            },
            onError: () => {
              setLoading(false);
            },
          });
        }
      } catch (error) {
        console.error('Error during submission:', error);
        setLoading(false);
      }
    },
  });

  const handleNameChange =
    (field: 'englishName' | 'arabicName') =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (value.length > 50) {
          formik.setFieldError(field, t('itemSubCategory.form.validation.categoryNameMaxLength'));
        } else {
          formik.setFieldValue(field, value);
          formik.setFieldError(field, undefined);
        }
      };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 4,
        mt: 2,
        bgcolor: '#fff',
        borderRadius: 1,
        border: '1px solid #ddd',
      }}
    >
      {/* {loading && (
        <Box sx={categoryStyles.loaderBox}>
          <CircularProgress />
        </Box>
      )} */}

      <Box 
        component="form" 
        noValidate 
        onSubmit={(e) => {
          console.log('Form submit triggered');
          console.log('Form is valid:', formik.isValid);
          console.log('Form errors:', formik.errors);
          formik.handleSubmit(e);
        }}
      >
        <Grid
          container
          direction="column"
          spacing={3}
          maxWidth="1200px"
          sx={{ width: '100%' }}
        >
          {/* Store Type Selection - Only show in add mode */}
          {/* {mode === 'add' && (
            <Grid>
              <Box sx={categoryStyles.labelBox}>
                <Typography sx={categoryStyles.labelText}>Select Store Type*</Typography>
                <FormControl fullWidth error={formik.touched.storeType && Boolean(formik.errors.storeType)}>
                  <Select
                    name="storeType"
                    value={formik.values.storeType}
                    displayEmpty
                    onChange={(e: any) => {
                      const value = e.target.value;
                      formik.setFieldValue('storeType', value);
                      formik.setFieldValue('storeId', '');

                    }}
                    sx={{ minWidth: 400 }}
                  >
                    <MenuItem value="" disabled>
                      <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>-- Select --</span>
                    </MenuItem>
                    <MenuItem value="Restaurant">Restaurant</MenuItem>
                    <MenuItem value="GroceryStore">Grocery Store</MenuItem>
                    <MenuItem value="PharmacyStore">Pharmacy Store</MenuItem>
                  </Select>
                  {formik.touched.storeType && formik.errors.storeType && (
                    <FormHelperText error sx={categoryStyles.errorText}>
                      {formik.errors.storeType}
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Grid>
          )} */}

          {/* Store Selection - Only show in add mode */}
          {/* {mode === 'add' && (
            <Grid>
              <Box sx={categoryStyles.labelBox}>
                <Typography sx={categoryStyles.labelText}>
                  Select Store*
                </Typography>
                <FormControl
                  fullWidth
                  error={
                    formik.touched.storeId &&
                    Boolean(formik.errors.storeId)
                  }
                  sx={{
                    alignItems: 'flex-start',
                    '& .MuiInput-root': {
                      backgroundColor: '#fff',
                      borderRadius: '4px',
                      padding: '8px 12px',
                      '&:hover:not(.Mui-disabled):before': {
                        borderBottom: '2px solid rgba(0, 0, 0, 0.42)',
                      },
                      '&:before': {
                        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                      },
                    },
                  }}
                >
                  <Autocomplete
                    options={storeOptions.filter(store => !formik.values.storeType || store.storeType === formik.values.storeType)}
                    getOptionLabel={(option) => option.label || ''}
                    value={storeOptions.find((opt) => opt.value === formik.values.storeId) || null}
                    onChange={(_, newValue) => {
                      const value = newValue ? newValue.value : '';
                      formik.setFieldValue('storeId', value);
                    }}
                    // disabled={!formik.values.storeType}
                    sx={{

                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        color: 'text.primary',
                        height: '45px',
                        width: '400px',
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        // placeholder={!formik.values.storeType ? "Select store type first" : "Select a store"}
                        placeholder="Select a store"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />
                  {formik.touched.storeId && formik.errors.storeId && (
                    <FormHelperText error sx={categoryStyles.errorText}>
                      {formik.errors.storeId}
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Grid>
          )} */}

          {/* English Name */}
          <Grid>
            <Box sx={categoryStyles.labelBox}>
              <Typography sx={categoryStyles.labelText}>
                {t('itemSubCategory.form.categoryNameEnglish')}
              </Typography>
              <FormControl
                fullWidth
                error={
                  formik.touched.englishName &&
                  Boolean(formik.errors.englishName)
                }
                sx={{
                  alignItems: 'flex-start',
                  '& .MuiInput-root': {
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: '2px solid rgba(0, 0, 0, 0.42)',
                    },
                    '&:before': {
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                    },
                  },
                }}
              >
                <TextField
                  placeholder={t('itemSubCategory.form.placeholders.categoryNameEnglish')}
                  name="englishName"
                  value={formik.values.englishName}
                  onChange={handleNameChange('englishName')}
                  onBlur={formik.handleBlur}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: 14,
                      color: 'text.primary',
                      width: '400px',
                      height: '45px',
                      marginBottom: '8px',
                    },
                    '& .MuiFormLabel-root': {
                      fontSize: 14,
                    },
                  }}
                />
                {formik.touched.englishName && formik.errors.englishName && (
                  <FormHelperText
                    error
                    sx={{ ...categoryStyles.errorText, mt: 1 }}
                  >
                    {formik.errors.englishName}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>
          </Grid>

          {/* Arabic Name */}
          <Grid>
            <Box sx={categoryStyles.labelBox}>
              <Typography sx={categoryStyles.labelText}>
                {t('itemSubCategory.form.categoryNameArabic')}
              </Typography>
              <FormControl
                fullWidth
                error={
                  formik.touched.arabicName && Boolean(formik.errors.arabicName)
                }
                sx={{
                  alignItems: 'flex-start',
                  '& .MuiInput-root': {
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: '2px solid rgba(0, 0, 0, 0.42)',
                    },
                    '&:before': {
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                    },
                  },
                }}
              >
                <TextField
                  placeholder={t('itemSubCategory.form.placeholders.categoryNameArabic')}
                  name="arabicName"
                  value={formik.values.arabicName}
                  onChange={handleNameChange('arabicName')}
                  onBlur={formik.handleBlur}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: 14,
                      color: 'text.primary',
                      width: '400px',
                      height: '45px',
                    },
                    '& .MuiFormLabel-root': {
                      fontSize: 14,
                    },
                  }}
                />
                {formik.touched.arabicName && formik.errors.arabicName && (
                  <FormHelperText
                    error
                    sx={{ ...categoryStyles.errorText, mt: 1 }}
                  >
                    {formik.errors.arabicName}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>
          </Grid>

          {/* Buttons */}
          <Grid>
            <Box sx={categoryStyles.buttonsWrapper}>
              <Box sx={{ minWidth: { xs: '100%', sm: '250px' } }} />
              <Stack
                direction="row"
                spacing={2}
                justifyContent="flex-start"
                sx={{ width: '100%' }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ bgcolor: primary.main }}
                 disabled={
                    mode === 'edit' &&
                    (!formik.dirty || !formik.isValid || loading)
                  }
                >
                  {loading ? t('itemSubCategory.form.saving') : mode === 'edit' ? t('itemSubCategory.form.update') : t('itemSubCategory.form.save')}
                </Button>
                <Button
                  variant="contained"
                  sx={categoryStyles.cancelButton}
                  onClick={() => {
                    formik.resetForm();
                    onCancel();
                  }}
                >
                  {t('itemSubCategory.form.cancel')}
                </Button>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default ItemSubCategoryForm;
